import React from 'react';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import Dropdown from '@mui/joy/Dropdown';
import MenuButton from '@mui/joy/MenuButton';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';

// Icons
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { themeColors, shadows } from '../../styles/theme';

export default function ModernProjectCard({
  project,
  workspace,
  onEdit,
  onDelete
}) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const projectColor = project.main_color || workspace?.main_color || themeColors.primary[500];

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: 'white',
        border: 'none',
        boxShadow: shadows.md,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'visible',
        borderRadius: 3,
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: shadows.xl,
          '& .card-actions': {
            opacity: 1,
          },
          '& .card-icon-bg': {
            backgroundColor: `${projectColor}25`,
            transform: 'scale(1.05)',
          },
        },
      }}
      onClick={() => onEdit(project)}
    >
      {/* Top Border Accent */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${projectColor}, ${projectColor}CC)`,
          borderRadius: '8px 8px 0 0',
        }}
      />

      {/* Card Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          {/* Icon */}
          <Box
            className="card-icon-bg"
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              background: `${projectColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: `inset 0 0 0 1px ${projectColor}20`,
            }}
          >
            <FolderIcon sx={{ fontSize: 28, color: projectColor }} />
          </Box>

          {/* Actions Menu */}
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  variant: 'plain',
                  size: 'sm',
                  className: 'card-actions',
                  sx: {
                    opacity: 0,
                    transition: 'all 0.2s ease',
                    backgroundColor: themeColors.neutral[50],
                    border: 'none',
                    '&:hover': {
                      backgroundColor: themeColors.neutral[100],
                      boxShadow: shadows.sm,
                    },
                  },
                },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertIcon />
            </MenuButton>
            <Menu placement="bottom-end" sx={{ minWidth: 160, zIndex: 1400, boxShadow: shadows.xl, border: 'none', borderRadius: 2.5 }}>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(project);
                }}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: themeColors.neutral[100],
                  },
                  transition: 'all 0.15s ease',
                }}
              >
                <EditIcon fontSize="small" />
                <Typography level="body-sm" sx={{ ml: 1.5 }}>Edit Flow</Typography>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/client/${workspace?.url_name}/${project.url_name}`, '_blank');
                }}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: themeColors.neutral[100],
                  },
                  transition: 'all 0.15s ease',
                }}
              >
                <OpenInNewIcon fontSize="small" />
                <Typography level="body-sm" sx={{ ml: 1.5 }}>Open Client View</Typography>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(project);
                }}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  color: themeColors.danger,
                  '&:hover': {
                    backgroundColor: `${themeColors.danger}10`,
                  },
                  transition: 'all 0.15s ease',
                }}
              >
                <DeleteIcon fontSize="small" />
                <Typography level="body-sm" sx={{ ml: 1.5 }}>Delete</Typography>
              </MenuItem>
            </Menu>
          </Dropdown>
        </Box>

        {/* Project Name */}
        <Typography
          level="title-lg"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: themeColors.neutral[900],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '3.5rem',
          }}
        >
          {project.name}
        </Typography>

        {/* Description */}
        <Typography
          level="body-sm"
          sx={{
            color: themeColors.neutral[600],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.5rem',
            mb: 2,
          }}
        >
          {project.description || 'No description provided'}
        </Typography>

        {/* URL Name */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: themeColors.neutral[100],
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          <Typography
            level="body-xs"
            sx={{
              fontFamily: 'monospace',
              color: themeColors.neutral[700],
              fontWeight: 600,
            }}
          >
            /{project.url_name}
          </Typography>
        </Box>
      </Box>

      {/* Card Footer */}
      <Box
        sx={{
          mt: 'auto',
          p: 3,
          pt: 2,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 16,
            right: 16,
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${themeColors.neutral[200]}, transparent)`,
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AccessTimeIcon sx={{ fontSize: 14, color: themeColors.neutral[500] }} />
          <Typography level="body-xs" sx={{ color: themeColors.neutral[600] }}>
            {formatDate(project.created_at)}
          </Typography>
        </Box>

        <Chip
          size="sm"
          variant="soft"
          sx={{
            backgroundColor: `${projectColor}15`,
            color: projectColor,
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        >
          Active
        </Chip>
      </Box>
    </Card>
  );
}
